import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Music, Megaphone, Sparkles, Disc3, QrCode, CalendarDays } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const SLIDE_MS = 8000;          // dwell per slide
const POLL_MS = 5000;           // backend poll interval

const resolveSrc = (u) => (u ? (u.startsWith('http') ? u : `${BACKEND}${u}`) : '');

/**
 * Full-screen TV/projector display.
 * Public route — no auth. Designed to be cast (Chromecast/AirPlay) or HDMI-mirrored.
 *
 * Rotating slides:
 *   1) NOW PLAYING — current track with cover art (always shown if a song is live)
 *   2) SHOUTOUT — approved guest shoutouts (one at a time, cycling)
 *   3) OFFER — venue offers (one at a time, cycling)
 *
 * Empty slide types are skipped automatically.
 */
const TVDisplayPage = () => {
  // Either `code` (legacy session-based: /tv/:code) OR `venueCode` (permanent: /:venueCode/tv)
  const { code, venueCode } = useParams();
  const [searchParams] = useSearchParams();
  const isVenueMode = !!venueCode;
  const lookupKey = (venueCode || code || '').toString();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [slideIdx, setSlideIdx] = useState(0);
  const pollRef = useRef(null);
  const slideRef = useRef(null);

  // Orientation — auto-detect from viewport (taller than wide → portrait),
  // or force via `?orientation=portrait|landscape` query param so wall-
  // mounted TVs whose OS reports the wrong aspect can be overridden.
  const orientationParam = (searchParams.get('orientation') || '').toLowerCase();
  const [autoPortrait, setAutoPortrait] = useState(
    () => typeof window !== 'undefined' && window.innerHeight > window.innerWidth,
  );
  useEffect(() => {
    const onResize = () => setAutoPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const portrait = orientationParam === 'portrait' || (orientationParam !== 'landscape' && autoPortrait);

  // Poll backend
  useEffect(() => {
    let stopped = false;
    const fetchOnce = async () => {
      try {
        const url = isVenueMode
          ? `${API}/venue/${lookupKey}/tv`   // permanent (resolved by venue_code, works offline)
          : `${API}/venue/tv/${lookupKey}`;  // legacy (resolved by active session code)
        const r = await axios.get(url);
        if (!stopped) { setData(r.data); setError(''); }
      } catch (e) {
        if (!stopped) setError(e?.response?.data?.detail || 'Display unavailable');
      }
    };
    fetchOnce();
    pollRef.current = setInterval(fetchOnce, POLL_MS);
    return () => { stopped = true; clearInterval(pollRef.current); };
  }, [lookupKey, isVenueMode]);

  // Build the slide reel from currently-available content. Reels react to new
  // shoutouts/offers/now-playing/events without losing your scroll position.
  const slides = (() => {
    if (!data) return [];
    const out = [];
    if (data.now_playing) out.push({ type: 'now_playing', payload: data.now_playing });
    (data.shoutouts || []).forEach((s) => out.push({ type: 'shoutout', payload: s }));
    (data.offers || []).forEach((o) => out.push({ type: 'offer', payload: o }));
    (data.events || []).forEach((e) => out.push({ type: 'event', payload: e }));
    if (!out.length) out.push({ type: 'idle' });
    return out;
  })();

  // Auto-rotate slides
  useEffect(() => {
    if (slides.length <= 1) return undefined;
    slideRef.current = setInterval(() => {
      setSlideIdx((i) => (i + 1) % slides.length);
    }, SLIDE_MS);
    return () => clearInterval(slideRef.current);
  }, [slides.length]);

  if (error) {
    return (
      <div className="fixed inset-0 bg-black text-white flex items-center justify-center" data-testid="tv-error">
        <div className="text-center">
          <p className="text-3xl font-extrabold text-[#FF007F] tracking-wider font-['Outfit']">SESSION OFFLINE</p>
          <p className="text-gray-400 mt-2">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="fixed inset-0 bg-black flex items-center justify-center"><Disc3 className="w-12 h-12 text-[#FF007F] animate-spin" /></div>;
  }

  const wallpaper = resolveSrc(data.venue_wallpaper_url);
  const logo = resolveSrc(data.venue_logo_url);
  const safeIdx = slides.length ? slideIdx % slides.length : 0;
  const slide = slides[safeIdx] || slides[0];

  return (
    <div
      className="fixed inset-0 bg-black text-white overflow-hidden select-none tv-scanlines"
      style={wallpaper ? { backgroundImage: `url(${wallpaper})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
      data-testid="tv-display-page"
    >
      {/* Ambient orbs DISABLED for TV performance — three large blurred
          gradient layers animating drift+scale was the #1 stutter source
          on Tizen / Android TV / WebOS. Replaced by a single static
          radial gradient on the dim overlay below. */}

      {/* Dim overlay so wallpaper doesn't drown content. A static radial
          accent gives subtle depth without forcing repaints. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 20% 20%, rgba(255, 0, 127, 0.18), transparent 55%), ' +
            'radial-gradient(ellipse at 80% 80%, rgba(0, 255, 255, 0.12), transparent 55%), ' +
            'linear-gradient(to bottom, rgba(0,0,0,0.72), rgba(0,0,0,0.55) 50%, rgba(0,0,0,0.88))',
        }}
      />

      {/* Top bar: logo + venue name + LIVE pulse */}
      <header className={`absolute top-0 left-0 right-0 z-10 flex items-center justify-between ${portrait ? 'px-6 py-6' : 'px-12 py-8'}`}>
        <div className="flex items-center gap-5">
          {logo ? (
            <img
              src={logo}
              alt={data.venue_name}
              className="w-20 h-20 rounded-2xl object-cover ring-2 ring-[#FF007F]/50 shadow-[0_0_40px_rgba(255,0,127,0.4)]"
              data-testid="tv-venue-logo"
            />
          ) : (
            <div className="w-20 h-20 rounded-2xl bg-[#FF007F]/15 ring-2 ring-[#FF007F]/40 flex items-center justify-center">
              <Disc3 className="w-9 h-9 text-[#FF007F]" />
            </div>
          )}
          <div>
            <p className="text-[10px] text-[#FF007F] uppercase tracking-[0.3em] font-bold">Live at</p>
            <h1 className="text-5xl font-extrabold tracking-tight font-['Outfit']" data-testid="tv-venue-name">
              {data.venue_name}
            </h1>
          </div>
        </div>
        <div
          className={`flex items-center gap-2 px-4 py-2 rounded-full border ${
            (isVenueMode && !data.is_live)
              ? 'bg-gray-500/10 border-gray-500/30'
              : 'bg-[#39FF14]/15 border-[#39FF14]/40'
          }`}
          data-testid="tv-live-pill"
        >
          <span className="relative flex w-2 h-2">
            {(isVenueMode && !data.is_live) ? (
              <span className="w-2 h-2 rounded-full bg-gray-500" />
            ) : (
              <>
                <span className="tv-ripple absolute inset-0 text-[#39FF14]" />
                <span className="w-2 h-2 rounded-full bg-[#39FF14]" />
              </>
            )}
          </span>
          <span
            className={`text-[11px] font-bold tracking-[0.25em] ${(isVenueMode && !data.is_live) ? 'text-gray-400' : 'text-[#39FF14]'}`}
          >
            {(isVenueMode && !data.is_live) ? 'OFFLINE' : 'LIVE'}
          </span>
        </div>
      </header>

      {/* Center: rotating slide. Key forces re-mount on slide change so the
          tv-slide-in animation re-fires every cycle. */}
      <main className={`absolute inset-0 flex items-center justify-center z-[2] ${portrait ? 'px-8 pt-40 pb-56' : 'px-16 py-32'}`}>
        <div className={`relative w-full tv-slide-in ${portrait ? 'max-w-3xl' : 'max-w-5xl'}`} key={`slide-${safeIdx}-${slide.type}`} data-testid={`tv-slide-${slide.type}`} data-orientation={portrait ? 'portrait' : 'landscape'}>
          {slide.type === 'now_playing' && (
            <div className={portrait ? "flex flex-col items-center gap-8 text-center" : "flex items-center gap-12"}>
              <div className="relative flex-shrink-0">
                {slide.payload.album_image ? (
                  <img
                    src={slide.payload.album_image}
                    alt=""
                    className="w-[260px] h-[260px] rounded-full object-cover ring-4 ring-white/10 tv-vinyl"
                    style={{ boxShadow: '0 0 30px rgba(255, 0, 127, 0.35)' }}
                  />
                ) : (
                  <div className="w-[260px] h-[260px] rounded-full bg-white/5 flex items-center justify-center tv-vinyl">
                    <Music className="w-24 h-24 text-white/20" />
                  </div>
                )}
                {/* Vinyl center spindle */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-6 h-6 rounded-full bg-black ring-2 ring-white/30" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-4 mb-3">
                  <p className="text-[#FF007F] text-sm font-extrabold tracking-[0.35em] flex items-center gap-2">
                    <Music className="w-4 h-4" /> NOW PLAYING
                  </p>
                  {/* Equalizer bars */}
                  <div className="flex items-end gap-1 h-6">
                    {[0,1,2,3,4,5].map((i) => (
                      <span
                        key={i}
                        className="tv-eq-bar w-1 rounded-full bg-[#FF007F]"
                        style={{ height: ['16px','22px','12px','26px','18px','24px'][i] }}
                      />
                    ))}
                  </div>
                </div>
                <p className="text-6xl font-extrabold text-white tracking-tight leading-tight font-['Outfit'] mb-3 line-clamp-2 tv-text-glow" data-testid="tv-now-playing-name">
                  {slide.payload.name}
                </p>
                <p className="text-2xl text-gray-300 truncate">
                  {(slide.payload.artists || []).join(', ')}
                </p>
              </div>
            </div>
          )}

          {slide.type === 'shoutout' && (
            slide.payload.photo_data_url ? (
              <div className={portrait ? "flex items-center gap-12 tv-pop-in flex-col text-center" : "flex items-center gap-12 tv-pop-in"} data-testid="tv-shoutout-with-photo">
                <img
                  src={slide.payload.photo_data_url}
                  alt="shoutout"
                  className="w-[320px] h-[320px] rounded-3xl object-cover ring-4 ring-[#39FF14]/30 flex-shrink-0"
                  style={{ boxShadow: '0 0 30px rgba(57, 255, 20, 0.3)' }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[#39FF14] text-sm font-extrabold tracking-[0.35em] mb-4 flex items-center gap-2">
                    <Megaphone className="w-5 h-5" /> SHOUTOUT
                  </p>
                  <p className="text-5xl font-extrabold text-white leading-tight font-['Outfit'] mb-5" data-testid="tv-shoutout-message">
                    &ldquo;{slide.payload.message}&rdquo;
                  </p>
                  <p className="text-2xl text-[#39FF14] tracking-wider font-semibold">— {slide.payload.guest_name}</p>
                </div>
              </div>
            ) : (
              <div className="text-center tv-pop-in">
                <p className="text-[#39FF14] text-sm font-extrabold tracking-[0.35em] mb-6 flex items-center justify-center gap-2">
                  <Megaphone className="w-5 h-5" /> SHOUTOUT
                </p>
                <p className="text-6xl font-extrabold text-white leading-tight font-['Outfit'] mb-6" data-testid="tv-shoutout-message">
                  &ldquo;{slide.payload.message}&rdquo;
                </p>
                <p className="text-2xl text-[#39FF14] tracking-wider font-semibold">— {slide.payload.guest_name}</p>
              </div>
            )
          )}

          {slide.type === 'offer' && (
            <div className="text-center relative">
              {/* Floating sparkles around the offer headline */}
              <div className="absolute inset-0 pointer-events-none" aria-hidden>
                {[
                  { left: '10%', top: '15%' }, { left: '85%', top: '20%' },
                  { left: '5%', top: '70%' }, { left: '92%', top: '75%' },
                  { left: '20%', top: '5%' }, { left: '75%', top: '90%' },
                ].map((s, i) => (
                  <Sparkles
                    key={i}
                    className="tv-sparkle absolute w-6 h-6 text-[#00FFFF]"
                    style={s}
                  />
                ))}
              </div>
              <p className="text-[#00FFFF] text-sm font-extrabold tracking-[0.35em] mb-6 flex items-center justify-center gap-2">
                <Sparkles className="w-5 h-5" /> OFFER OF THE NIGHT
              </p>
              <p className="text-7xl font-extrabold text-white leading-tight font-['Outfit']" data-testid="tv-offer-text">
                {slide.payload}
              </p>
            </div>
          )}

          {slide.type === 'event' && (
            <div className={portrait ? "text-center flex flex-col items-center" : "flex items-center gap-12"} data-testid="tv-slide-event">
              {slide.payload.poster_url ? (
                <img
                  src={resolveSrc(slide.payload.poster_url)}
                  alt=""
                  className={portrait
                    ? "w-[70vw] max-w-[520px] aspect-[3/4] rounded-3xl object-cover ring-4 ring-[#FF007F]/40 mb-8"
                    : "w-[380px] h-[520px] rounded-3xl object-cover ring-4 ring-[#FF007F]/40 flex-shrink-0"}
                  style={{ boxShadow: '0 0 40px rgba(255, 0, 127, 0.35)' }}
                />
              ) : (
                <div className={portrait
                  ? "w-[70vw] max-w-[520px] aspect-[3/4] rounded-3xl bg-gradient-to-br from-[#FF007F]/30 to-[#9D00FF]/30 flex items-center justify-center mb-8"
                  : "w-[380px] h-[520px] rounded-3xl bg-gradient-to-br from-[#FF007F]/30 to-[#9D00FF]/30 flex items-center justify-center flex-shrink-0"}>
                  <CalendarDays className="w-24 h-24 text-white/40" />
                </div>
              )}
              <div className={portrait ? "text-center" : "flex-1 min-w-0"}>
                <p className={`text-[#FF007F] ${portrait ? 'text-lg' : 'text-sm'} font-extrabold tracking-[0.35em] mb-4 flex items-center ${portrait ? 'justify-center' : ''} gap-2`}>
                  <CalendarDays className={portrait ? "w-6 h-6" : "w-5 h-5"} /> UPCOMING EVENT
                </p>
                <h2
                  className={`font-extrabold text-white leading-tight font-['Outfit'] mb-6 ${portrait ? 'text-6xl' : 'text-6xl'}`}
                  data-testid="tv-event-name"
                >
                  {slide.payload.name}
                </h2>
                <div className={`flex items-center gap-3 ${portrait ? 'justify-center flex-wrap' : ''} mb-4`}>
                  <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF007F]/15 border border-[#FF007F]/40 ${portrait ? 'text-xl' : 'text-2xl'} font-bold text-[#ff7ab1] tracking-wide`}>
                    {new Date(`${slide.payload.event_date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
                  </span>
                  {slide.payload.start_time && (
                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/15 ${portrait ? 'text-xl' : 'text-2xl'} font-bold text-white tracking-wide`}>
                      {slide.payload.start_time}
                      {slide.payload.end_time && ` – ${slide.payload.end_time}`}
                    </span>
                  )}
                  {slide.payload.is_all_day && (
                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/15 ${portrait ? 'text-xl' : 'text-2xl'} font-bold text-white tracking-wide`}>All day</span>
                  )}
                </div>
                {slide.payload.description && (
                  <p className={`text-gray-300 ${portrait ? 'text-lg max-w-[80vw]' : 'text-xl max-w-xl'} line-clamp-3 font-medium mx-auto`}>
                    {slide.payload.description}
                  </p>
                )}
              </div>
            </div>
          )}

          {slide.type === 'idle' && (
            <div className="text-center">
              <Disc3 className="w-24 h-24 text-[#FF007F]/40 animate-spin-slow mx-auto mb-6" />
              <p className="text-3xl text-gray-400 font-['Outfit']">
                {(isVenueMode && !data.is_live)
                  ? `Welcome to ${data.venue_name || 'the venue'} — the music kicks in soon`
                  : 'Waiting for the show to start…'}
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Bottom: slide dots on left, QR on bottom-right */}
      <footer className={`absolute bottom-0 left-0 right-0 z-10 flex items-end justify-between ${portrait ? 'px-6 py-6 flex-col gap-4' : 'px-12 py-8'}`}>
        {slides.length > 1 ? (
          <div className="flex gap-2 items-center" data-testid="tv-slide-dots">
            {slides.map((s, i) => (
              <span
                key={`dot-${s.type}-${s.payload?.id || s.payload?.name || s.payload || i}`}
                className={`h-1.5 rounded-full transition-all duration-500 relative overflow-hidden ${
                  i === safeIdx ? 'w-20 bg-white/15' : 'w-1.5 bg-white/30'
                }`}
              >
                {i === safeIdx && (
                  <span
                    key={`fill-${safeIdx}`}
                    className="tv-progress-fill absolute inset-0 bg-[#FF007F] rounded-full"
                  />
                )}
              </span>
            ))}
          </div>
        ) : (
          <div />
        )}

        <div className="flex items-center gap-4 bg-black/75 rounded-2xl px-5 py-4 border border-white/10" data-testid="tv-qr-block">
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-[0.25em] font-bold flex items-center gap-1.5 justify-end">
              <QrCode className="w-3 h-3" /> Scan to interact
            </p>
            <p className="text-sm text-white font-semibold mt-0.5 text-right">Request songs · Send shoutouts · View menu</p>
          </div>
          <div className="bg-white p-1.5 rounded-lg">
            <QRCodeSVG
              value={
                isVenueMode
                  ? `${BACKEND}/v/${data.venue_code || lookupKey}`
                  : `${BACKEND}/request/${lookupKey}`
              }
              size={70}
              level="M"
              fgColor="#000000"
            />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TVDisplayPage;
